import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Box, Dialog, DialogActions, DialogContent, Button, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import "./fonts.css";
import "./App.css";
import tokenManager from "./services/tokenManager";
import { refreshAccessToken } from "./services/apiClient";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import LoadingGif from "./components/LoadingGif";
import PageLoadingFallback from "./components/PageLoadingFallback";
import SplashScreen from "./components/SplashScreen";
import { preloadImages, ALL_PUBLIC_IMAGES } from "./utils/imagePreloader";
import { NotificationProvider } from "./contexts/NotificationContext";

const Login = lazy(() => import("./pages/Login"));
const Geolocation = lazy(() => import("./pages/Geolocation"));
const Info = lazy(() => import("./pages/Info"));
const PetManagementHome = lazy(() => import("./pages/PetManagementHome"));
const Account = lazy(() => import("./pages/Account"));
const QrVerification = lazy(() => import("./pages/QrVerification"));
const SupplierPage = lazy(() => import("./pages/SupplierPage"));
const Supplier = lazy(() => import("./pages/Supplier"));
const TagUpdatePage = lazy(() => import("./pages/TagUpdatePage"));
const CheckTag = lazy(() => import("./pages/Check_Tag"));
const PTagAirConfirmation = lazy(() => import("./pages/PTagAirConfirmation"));
const PTag = lazy(() => import("./pages/PTag"));
const PTagPayment = lazy(() => import("./pages/PTagPayment"));
const PTagAir = lazy(() => import("./pages/PTagAir"));
const PTagAirPayment = lazy(() => import("./pages/PTagAirPayment"));
const PetBasic = lazy(() => import("./pages/PetBasic"));
const PetDetail = lazy(() => import("./pages/PetDetail"));
const PetMedical = lazy(() => import("./pages/PetMedical"));
const PetMedication = lazy(() => import("./pages/PetMedication"));
const PetDeworming = lazy(() => import("./pages/PetDeworming"));
const PetVaccination = lazy(() => import("./pages/PetVaccination"));
const PetFaceID = lazy(() => import("./pages/PetFaceID"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPage"));
const ShoppingCart = lazy(() => import("./pages/ShoppingCart"));
const PetLostMember = lazy(() => import("./pages/PetLostMember"));
const PetLostReport = lazy(() => import("./pages/PetLostReport"));
const PetLostConfirmation = lazy(() => import("./pages/PetLostConfirmation"));
const PetFoundReport = lazy(() => import("./pages/PetFoundReport"));
const AllLostPets = lazy(() => import("./pages/AllLostPets"));
const AllLostPetsAdmin = lazy(() => import("./pages/AllLostPetsAdmin"));
const PetSearchPage = lazy(() => import("./pages/PetSearchPage"));
const PetSearchResult = lazy(() => import("./pages/PetSearchResult"));
const PetFoundSearch = lazy(() => import("./pages/PetFoundSearch"));
const PetLostSearch = lazy(() => import("./pages/PetLostSearch"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const PetTransfer = lazy(() => import("./pages/PetTransfer"));
const Notification = lazy(() => import("./pages/Notification"));
const Adoption = lazy(() => import("./pages/Adoption"));
const PetActivities = lazy(() => import("./pages/PetActivities"));

const ACCESS_TOKEN_REFRESH_INTERVAL_MS = 8 * 60 * 1000;
const LOGGED_OUT_FLAG = "ptag_logged_out";

//scrolling
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // Run before browser paints
    const scrollToTop = () => {
      // Method 1: Standard
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'   // 'instant' is more reliable than 'auto'
      });

      // Method 2: Force on html and body
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;

      // Method 3: Scroll any possible main scroll container
      const containers = document.querySelectorAll('.app, .hotel-detail-page, main, #root, .content-wrapper');
      containers.forEach(container => {
        if (container) container.scrollTop = 0;
      });
    };

    // Small timeout helps when there is heavy content or images
    scrollToTop();
    const timeout = setTimeout(scrollToTop, 0);
    const timeout2 = setTimeout(scrollToTop, 50);

    return () => {
      clearTimeout(timeout);
      clearTimeout(timeout2);
    };
  }, [pathname]);

  return null;
}

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpiredDialog(true);
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      // Preload in the background; don't block first render on it.
      preloadImages(ALL_PUBLIC_IMAGES);
      // Check if token exists in memory
      let token = tokenManager.getAuthToken();

      // If not in memory, try cookies (backward compatibility)
      if (!token) {
        token = Cookies.get('token');
        if (token) {
          const userId = Cookies.get('id');
          const userRole = Cookies.get('role');
          tokenManager.setAuthToken(token);
          tokenManager.setUserId(userId);
          tokenManager.setUserRole(userRole);
        }
      }

      // A guest session has neither an access token nor a user id. Do not call
      // the refresh endpoint in that case: the endpoint correctly returns 401
      // when there is no refresh-token cookie, but that is not an application
      // error for a guest.
      if (!token) {
        if (localStorage.getItem(LOGGED_OUT_FLAG) === "true") {
          setIsInitialized(true);
          if (window.__splashPageReady) {
            window.__splashPageReady();
          }
          return;
        }

        const hasRefreshSession = Boolean(
          Cookies.get('id') || Cookies.get('refreshToken')
        );

        if (hasRefreshSession) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            console.log('Session restored via refresh token');
          }
        } else {
          console.log('No authenticated session found; continuing as guest');
        }
      } else {
        console.log('Session restored from memory');
      }

      setIsInitialized(true);

      // Signal to SplashScreen that the app is ready
      if (window.__splashPageReady) {
        window.__splashPageReady();
      }
    };

    restoreSession();
  }, []);

  useEffect(() => {
    if (!isInitialized) return undefined;

    const refreshActiveSession = async () => {
      const token = tokenManager.getAuthToken() || Cookies.get('token');
      const userId = tokenManager.getUserId() || Cookies.get('id');
      if (localStorage.getItem(LOGGED_OUT_FLAG) === "true" || !token || !userId) return;

      const newToken = await refreshAccessToken();
      if (newToken) {
        Cookies.set("token", newToken);
      }
    };

    const refreshInterval = setInterval(refreshActiveSession, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [isInitialized]);

  const handleSplashFinish = () => {
    setShowSplash(false);

    // Hide the static HTML splash screen
    const initialSplash = document.getElementById('initial-splash');
    if (initialSplash) {
      initialSplash.style.display = 'none';
    }
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <LoadingGif />
      </Box>
    );
  }

  return (
    <div className="app">
      <Helmet>
        <title>PTag - 關懷原於這裡，一個混合科技與藝術的寵物名牌</title>
        <meta property="og:title" content="Ptag" />
        <meta property="og:site_name" content="Ptag" />
        <meta property="og:url" content="https://www.ptag.com.hk/" />
        <meta
          property="og:image"
          content="https://www.ptag.com.hk/img/fb_display.jpg"
        />
        <meta
          property="og:description"
          content="關懷原於這裡，一個混合科技與藝術的寵物牌"
        />
        <meta property="fb:app_id" content="607616469433776" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <link
          rel="alternate"
          hreflang="vi"
          href="https://vi-vn.facebook.com/"
        />
        <meta
          name="keywords"
          content="Pet, PTag, Pet Tag, Lost, Protect, Protection, 寵物, 寵物牌, 保護, 保障, 智能寵物牌, 健康, 失寵, 失狗, 失貓, 尋狗, 尋貓, 尋寵, 寵物展, 推介產品, 寵物名牌, 寵物走失, 自訂寵物牌"
        />
        <meta
          name="description"
          content="PTag - 關懷，原於這裡。一個混合科技與藝術的寵物名牌。體積很細，功能很大。PTag，一個寵物資料庫，給你的愛寵多一個保障。"
        />
        <meta name="robots" content="noodp,noydir" />
        <meta name="author" content="Pet Boys Club, 寵物仔仔" />
      </Helmet>

      <NotificationProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />

            <Route path="/aboutus" element={<AboutUs />} />

            <Route path="/landing" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/qr_verification" element={<QrVerification />} />
            <Route path="/supplier" element={<Supplier />} />
            <Route path="/supplier-page" element={<SupplierPage />} />
            <Route path="/check_tag" element={<CheckTag />} />
            <Route path="/tag_update_page" element={<TagUpdatePage />} />
            <Route path="/shopping_cart" element={<ShoppingCart />} />

            <Route path="/pet_lost_member" element={<PetLostMember />} />
            <Route path="/pet_lost_report" element={<PetLostReport />} />
            <Route path="/pet_lost_confirmation" element={<PetLostConfirmation />} />
            <Route path="/pet_found_report" element={<PetFoundReport />} />
            <Route path="/pet_found_search" element={<PetFoundSearch />} />
            <Route path="/all_lost_pets" element={<AllLostPets />} />
            <Route path="/all_lost_pets_admin" element={<AllLostPetsAdmin />} />
            <Route path="/pet_search_page" element={<PetSearchPage />} />
            <Route path="/pet_search_result" element={<PetSearchResult />} />
            <Route path="/pet_lost_search" element={<PetLostSearch />} />


            {/* For managing qr code */}
            <Route path="/php/qr_info.php" element={<Landing />} />

            {/* 地理位置页面路由 */}
            <Route path="/geolocation" element={<Geolocation />} />
            <Route
              path="/php/qr_info_geolocation.php"
              element={<Geolocation />}
            />
            <Route
              path="/php/qr_info_geolocation_amap.php"
              element={<Geolocation />}
            />

            <Route
              path="/php/purchase_air.php"
              element={<PTagAir />}
            />


            {/* 宠物详情页面路由 */}
            <Route path="/pet_info" element={<Info />} />
            <Route path="/php/pet_detail_home.php" element={<Info />} />
            <Route path="/register" element={<Login initialTab="register" />} />

            {/* 宠物管理页面路由 */}
            <Route path="/pet-management" element={<PetManagementHome />} />
            <Route path="/pet-management/add" element={<PetBasic />} />
            <Route path="/pet-management/add/details" element={<PetDetail />} />

            <Route path="/pet-management/edit/:petId" element={<PetBasic />} />
            <Route
              path="/pet-management/edit/:petId/details"
              element={<PetDetail />}
            />
            <Route
              path="/pet-management/edit/:petId/activities"
              element={<PetActivities />}
            />
            <Route
              path="/pet-management/edit/:petId/medical"
              element={<PetMedical />}
            />
            <Route
              path="/pet-management/edit/:petId/medication"
              element={<PetMedication />}
            />
            <Route
              path="/pet-management/edit/:petId/deworming"
              element={<PetDeworming />}
            />
            <Route
              path="/pet-management/edit/:petId/vaccination"
              element={<PetVaccination />}
            />
            <Route
              path="/pet-management/edit/:petId/faceID"
              element={<PetFaceID />}
            />
            <Route
              path="/pet-management/edit/:petId/pet_transfer"
              element={<PetTransfer />}
            />
            <Route path="/ptag" element={<PTag />} />
            <Route path="/ptag-air" element={<PTagAir />} />
            <Route path="/ptag/payment" element={<PTagPayment />} />
            <Route path="/ptag-air/payment" element={<PTagAirPayment />} />
            <Route path="/ptag-air/confirmation" element={<PTagAirConfirmation />} />
            <Route path="/account/:id" element={<Account />} />

            <Route path="/notification" element={<Notification />} />

            <Route path="/adoption" element={<Adoption />} />
          </Routes>
          </Suspense>
        </BrowserRouter>

        <Dialog
          open={showSessionExpiredDialog}
          onClose={() => setShowSessionExpiredDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              maxWidth: 400,
              width: "100%",
              p: 3,
              m: 2,
              boxSizing: "border-box",
              textAlign: "center",
            },
          }}
        >
          <DialogContent sx={{ pt: 0, pb: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}
            >
              {t("SessionExpiredTitle")}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: 16, color: "#666" }}>
              {t("SessionExpiredMessage")}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: "column",
              "& > :not(style) ~ :not(style)": {
                marginLeft: 0,
              },
            }}
          >
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: "#FFB60C",
                color: "#050505",
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                py: 1,
                mb: 1,
                textAlign: "center",
                textTransform: "none",
                "&:hover": { backgroundColor: "#e8a300" },
              }}
              onClick={() => {
                // Clear tokens from memory (primary storage)
                tokenManager.clear();
                localStorage.setItem(LOGGED_OUT_FLAG, "true");

                // Clear cookies (backward compatibility)
                Cookies.remove("token");
                Cookies.remove("id");
                Cookies.remove("userRole");
                Cookies.remove("refreshToken");
                Cookies.remove("refreshToken", { path: "/" });
                Cookies.remove("refreshToken", { path: "/auth/tokens/refresh" });
                Cookies.remove("refreshToken", { path: "/development/auth/tokens/refresh" });
                Cookies.remove("notifications");
                Cookies.remove("role");

                // Update UI state
                setShowSessionExpiredDialog(false);
                setShowSplash(false);
                window.location.href = "/login";
              }}
            >
              {t("loginFooter")}
            </Button>
            <Button
              fullWidth
              variant="text"
              sx={{
                color: "#B0B0B0",
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                py: 1,
                textAlign: "center",
                textTransform: "none",
              }}
              onClick={() => setShowSessionExpiredDialog(false)}
            >
              {t("cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      </NotificationProvider>
    </div>
  );
};

export default App;
